import { createRouter, createWebHistory } from 'vue-router'

import { Permission } from '@/enums/permission'
import { isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (to.hash)               return { el: to.hash }
        if (from.path === to.path) return {}

        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // Free and taken, centre by centre: the answer to "have you got anything at
            // four", which used to live only on the desk's screen.
            path:      '/koledar',
            name:      'calendar',
            component: () => import('@/views/facility/Calendar.vue'),
            meta:      { requiresAuth: true, permission: Permission.FacilityWork },
        },
        {
            // Where whoever works the centres lands: the day's appointments for the
            // person who gives them, and the day's hires for the person who hands them
            // out. It is the root path rather than /services, because this portal is
            // that screen — they open a bookmark, not a menu.
            path:      '/',
            name:      'services',
            component: () => import('@/views/facility/Services.vue'),
            meta:      { requiresAuth: true, permission: Permission.FacilityWork },
        },
    ],
})

router.beforeEach(async (to) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    if (loggedIn && to.name === 'login') return { name: 'services' }

    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()

        return { name: 'login' }
    }

    if (!to.meta.requiresAuth || !loggedIn) return true

    // Imported here rather than at the top of the file on purpose: the session helper
    // reaches the fetch store, which reaches this router, and a static cycle would
    // leave one of the three undefined at evaluation time depending on which module
    // the bundler happened to start with.
    const { loadSession } = await import('@/libs/session')

    await loadSession()

    // This portal is one screen, and the permission for it is the one the API guards
    // the same work with. An account without it has nothing here at all and is told so
    // on the screen rather than bounced between two addresses.
    // No redirect for an account that lacks the permission: this portal has one screen,
    // and sending it to itself is a loop the router refuses. The screen says there is
    // nothing here for them, which is the truth and is somewhere to read it.
    return true
})

/**
 * A screen whose code is no longer on the server.
 *
 * Every screen is loaded when it is first opened. Deploying a new version replaces
 * those files with differently named ones, so a browser tab that was open across a
 * deploy asks for a file that no longer exists — and the click does nothing at all.
 * No error, no message: the person presses "Objekti" and the screen stays where it is.
 *
 * The tab heals itself by loading the new version once, at the address the person was
 * going to. The flag is what keeps that from becoming a loop when the failure is
 * something else — a portal that reloads forever is worse than one that does nothing.
 */
const RELOAD_FLAG = 'pms:reloaded-for-chunk'

const isMissingChunk = (error: unknown): boolean => {
    const message = error instanceof Error ? error.message : String(error)

    return /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(message)
}

router.onError((error, to) => {
    if (!isMissingChunk(error)) return

    if (sessionStorage.getItem(RELOAD_FLAG)) return

    sessionStorage.setItem(RELOAD_FLAG, '1')
    window.location.assign(to.fullPath)
})

// A navigation that worked is the proof the code is current, so the guard is cleared
// and the next deploy gets its one reload too.
router.afterEach(() => sessionStorage.removeItem(RELOAD_FLAG))

export default router
